import React from 'react'
import {useNavigate} from 'react-router'
import {useSelector} from 'react-redux'
import {useAuth0} from '@auth0/auth0-react'
import {Modal} from '@thryvlabs/maverick'
import {catalogbg} from '../../../assets'
import {KP11LinkCatalogComponent} from './kp-11Link-catalog/kp-11Link-catalog'
import {
  KP11LayoutCatalogWrapStyle,
  KP11LayoutContainerStyle,
  KP11LayoutOrderSummaryWrapStyle,
} from './kp-11Link-layout-style'
import {useKP11LinkData} from './kp-11Link-utility/kp11-link-use-data'
import {KP11LinkOrderSummaryComponent} from './kp-11Link-order-summary/kp-11Link-order-summary'
import {selectPartnerAC} from '../../../app-store/selector'
import {getUserRoles} from '../../../utils/get-user-roles'
import {
  KEAP_ADDONS_PARENT_CODES,
  KEAP_LEGACY_CONTACTS_CODES,
} from '../../../utils/constants'
import {KP11LegacyContactsBlockModal} from './kp-11Link-reusable-components/kp-11Link-legacy-contacts-modal'
import {buildKP11CustomerInfo} from './kp-11Link-customer-info'

// --- Simple Skeleton Components ---
const SkeletonBox = ({width = '100%', height = '20px', style = {}}) => (
  <div
    style={{
      width,
      height,
      borderRadius: '6px',
      background: 'linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.2s infinite linear',
      ...style,
    }}
  />
)

const SkeletonLoader = () => (
  <KP11LayoutContainerStyle
    style={{
      padding: '2rem',
      height: '100%',
      gridTemplateColumns: '1fr',
      backgroundImage: `url(${catalogbg})`,
      backgroundSize: '100% 60%',
      backgroundPosition: 'top center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
    }}
  >
    <style>
      {`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}
    </style>

    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 3fr) minmax(0, 2fr)`,
        gap: '2rem',
        width: '100%',
        height: '100%',
      }}
    >
      {/* Catalog skeleton */}
      <div
        style={{
          background: '#fff',
          borderRadius: '10px',
          padding: '1.5rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SkeletonBox width="50%" height="24px" style={{marginBottom: '1rem'}} />
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {Array.from({length: 6}).map((_, i) => (
            <SkeletonBox key={i} height="40px" style={{margin: '8px 0'}} />
          ))}
        </div>
      </div>

      {/* Order summary skeleton */}
      <div
        style={{
          background: '#fff',
          borderRadius: '10px',
          padding: '1.5rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          height: '100%', // fill full height
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SkeletonBox width="60%" height="24px" style={{marginBottom: '1rem'}} />
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {Array.from({length: 6}).map((_, i) => (
            <SkeletonBox key={i} height="28px" style={{margin: '10px 0'}} />
          ))}
          <SkeletonBox
            width="40%"
            height="32px"
            style={{
              marginTop: '2rem',
              display: 'flex',
              justifyContent: 'center',
            }}
          />
        </div>
      </div>
    </div>
  </KP11LayoutContainerStyle>
)

export const MissingKeapPartnerIdModal = () => {
  const navigate = useNavigate()
  return (
    <Modal
      altBtn={<></>}
      openOnLoad
      variant="default"
      footer
      action
      btnAction={() => {
        navigate('/')
      }}
      btnActionText="Ok"
      hideX
      disableX
      disableBackdropClick
      btnType="primary"
    >
      <div style={{padding: '20px', textAlign: 'center'}}>
        <h3
          style={{
            color: '#FF5000',
            marginBottom: '12px',
            fontSize: '20px',
            fontWeight: '600',
          }}
        >
          Keap Partner ID Not Found
        </h3>
        <p
          style={{
            whiteSpace: 'pre-wrap',
            marginTop: '16px',
            marginBottom: '0',
            color: '#333',
            fontSize: '14px',
            lineHeight: '1.6',
          }}
        >
          {`Please make sure you are logging in from the link in Salesforce.\nIf the problem persists reach out to your RPM for assistance.`}
        </p>
      </div>
    </Modal>
  )
}

export const KP11LinkLayoutComponent = ({keapPartnerId}) => {
  // Get all data and handlers from the centralized hook
  const {
    isLoading,
    selectedCountry,
    keapPartnerDetails,
    accountInfoWithSub,
    thryvAccount,
    centersNAppVList,
    catalogGroups,
    setCatalogGroups,
    allGroupCustomFieldsMap,
    setAllGroupCustomFieldsMap,
    defaultAllGroupCustomFieldsMap,
    defaultCartCentersNApps,
    cartCentersAndApps,
    allCoupons,
    plansIncentiveGroups,
    removeCoupon,
    togglePlanIncentive,
    fetchAndApplyPromoCode,
  } = useKP11LinkData()

  const partnerAffiliateCode = useSelector(selectPartnerAC)
  const {user} = useAuth0()
  const userRoles = keapPartnerDetails?.user_roles || getUserRoles(user)
  const isInfluencerOps = userRoles.includes('InfluencerOps')

  // Normalize card identity across route state, validate-email, and subscription fallbacks.
  const customerInfo = buildKP11CustomerInfo({
    accountInfoWithSub,
    thryvAccount,
  })

  if (isLoading) return <SkeletonLoader />

  // Block order if the account has legacy contacts that require Client Care handling.
  // Legacy contact codes are nested inside the addons[] of the keap addons parent
  // object (KEAP_ADDONS_PARENT_CODES) which lives in defaultCartCentersNApps.apps.
  // const hasLegacyContacts = (thryvAccount?.childSubscriptions || []).some(
  //   item =>
  //     item.isAlreadyAdded &&
  //     (item.addons || []).some(addon =>
  //       KEAP_LEGACY_CONTACTS_CODES.includes(addon.code),
  //     ),
  // )
  const hasLegacyContacts = (thryvAccount?.childSubscriptions || []).some(
    item =>
      KEAP_ADDONS_PARENT_CODES.includes(item.code) &&
      (item.addons || []).some(addon =>
        KEAP_LEGACY_CONTACTS_CODES.includes(addon.code),
      ),
  )

  if (hasLegacyContacts) {
    return <KP11LegacyContactsBlockModal />
  }

  // Validate Keap Partner ID before allowing user to proceed.
  // Sources mirror kp-11Link-generateUrl.js priority ladder:
  //   1. InfluencerOps → user_metadata.networkId
  //   2. Switched/native KeapPartner → partnerAffiliateCode (Redux)
  //   3. URL ?pac= / sessionStorage → keapPartnerId (Redux user slice)
  //   4. Salesforce-sourced metadata → user_metadata.keapPartnerId
  const partnerIdFromMetadata = keapPartnerDetails?.user_metadata?.keapPartnerId
  if (
    !isInfluencerOps &&
    !keapPartnerId &&
    !partnerIdFromMetadata &&
    !partnerAffiliateCode
  ) {
    return <MissingKeapPartnerIdModal />
  }

  return (
    <KP11LayoutContainerStyle>
      <KP11LayoutCatalogWrapStyle
        style={{
          backgroundImage: `url(${catalogbg})`,
          backgroundSize: '100% 60%',
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          width: '100%',
        }}
      >
        <KP11LinkCatalogComponent
          selectedCountry={selectedCountry}
          centersNAppVList={centersNAppVList}
          catalogGroups={catalogGroups}
          setCatalogGroups={setCatalogGroups}
          allGroupCustomFieldsMap={allGroupCustomFieldsMap}
          setAllGroupCustomFieldsMap={setAllGroupCustomFieldsMap}
          defaultAllGroupCustomFieldsMap={defaultAllGroupCustomFieldsMap}
          keapPartnerId={keapPartnerId}
          defaultCartCentersNApps={defaultCartCentersNApps}
          customerInfo={customerInfo}
        />
      </KP11LayoutCatalogWrapStyle>

      <KP11LayoutOrderSummaryWrapStyle>
        <KP11LinkOrderSummaryComponent
          selectedCountry={selectedCountry}
          keapPartnerDetails={keapPartnerDetails}
          accountInfoWithSub={accountInfoWithSub}
          thryvAccount={thryvAccount}
          cartCentersAndApps={cartCentersAndApps}
          defaultCartCentersNApps={defaultCartCentersNApps}
          centersNAppVList={centersNAppVList}
          allCoupons={allCoupons}
          removeCoupon={removeCoupon}
          plansIncentiveGroups={plansIncentiveGroups}
          togglePlanIncentive={togglePlanIncentive}
          fetchAndApplyPromoCode={fetchAndApplyPromoCode}
          keapPartnerId={keapPartnerId}
        />
      </KP11LayoutOrderSummaryWrapStyle>
    </KP11LayoutContainerStyle>
  )
}
